import { html, LitElement } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { property } from 'lit/decorators/property.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { ComposedEvent } from '@patternfly/pfe-core';
import { bound, observed } from '@patternfly/pfe-core/decorators.js';
// @ts-ignore
import { RhDialog } from '@rhds/elements/rh-dialog/rh-dialog.js';

import styles from './rhpt-dialog.css';

export class DialogToggleEvent extends ComposedEvent {
  constructor(
    /** Current open state of the dialog  */
    public open: RhDialog['open']
  ) {
    super('dialog-toggle', {
      bubbles: true,
      composed: true,
    });
  }
}

@customElement('rhpt-dialog')
export class RhptDialog extends LitElement {
  static readonly shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true };

  static styles = [styles];

  @observed
  @property()
    trigger?: string;

  #activeTrigger?: HTMLElement;
  #triggerRefs = new Set<HTMLElement>();

  #rhDialog = createRef<RhDialog>();
  #mo = new MutationObserver(this.onDialogMutation);

  protected firstUpdated(): void {
    if (this.#rhDialog.value) {
      this.#mo.observe(this.#rhDialog.value, { attributes: true });
    }
  }

  public toggle() {
    if (!this.#rhDialog.value) {
      return;
    }
    // open the dialog
    this.#rhDialog.value.toggle();
    this.dispatchEvent(new DialogToggleEvent(this.#rhDialog.value.open));
  }

  render() {
    return html`
      <rh-dialog ${ref(this.#rhDialog)}>
        <slot name="header" slot="header"></slot>
        <slot></slot>
        <slot name="footer" slot="footer"></slot>
      </rh-dialog>
    `;
  }

  /** Set up click handlers for all triggers */
  protected _triggerChanged() {
    if (!this.trigger) {
      // clear existing refs
      for (const trigger of this.#triggerRefs) {
        this.#clearTriggerEvents(trigger);
      }
      return;
    }
    Array.from(document.querySelectorAll(this.trigger)).forEach(el => {
      if (!(el instanceof HTMLElement)) {
        return;
      }
      // if the trigger is not in the map then apply
      // the click handler and add it to the WeakMap
      if (!this.#triggerRefs.has(el)) {
        this.#addTriggerEvents(el);
      }
    });
  }

  #addTriggerEvents(trigger: HTMLElement) {
    trigger.addEventListener('click', this.onTriggerClick);
    trigger.addEventListener('keydown', this.onTriggerKeydown);
    this.#triggerRefs.add(trigger);
  }

  #clearTriggerEvents(trigger: HTMLElement) {
    trigger.removeEventListener('click', this.onTriggerClick);
    trigger.removeEventListener('keydown', this.onTriggerKeydown);
    this.#triggerRefs.delete(trigger);
  }

  /**
   * When the open attr changes to closed on rh-dialog
   * then return the focus to the trigger.
   */
  @bound
  private onDialogMutation(e: MutationRecord[]) {
    for (const mut of e) {
      if (mut.attributeName === 'open') {
        if (this.#rhDialog?.value?.open !== undefined) {
          if (this.#rhDialog.value.open === false) {
            this.#activeTrigger?.focus?.();
            this.#activeTrigger = undefined;
          }
          this.dispatchEvent(new DialogToggleEvent(this.#rhDialog.value.open));
        }
      }
    }
  }

  @bound
  private async onTriggerClick(event: Event) {
    event.preventDefault();
    this.toggle();
  }

  @bound
  private async onTriggerKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter') {
      return;
    }
    if (event.target) {
      // remember what trigger initiated the
      // dialog so we can return focus to it later.
      this.#activeTrigger = event.target as HTMLElement;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rhpt-dialog': RhptDialog;
  }
}
